import { useEffect, useState } from 'react';
import type { Ticket } from '../types';
import { relativeTime } from '../util';

interface Props {
  ticket: Ticket;
  onClose: () => void;
}

export function TicketDetailSheet({ ticket, onClose }: Props) {
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  async function copy(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  }

  const c = ticket.classification;
  const body = c?.body_md || ticket.raw_input.text || '';

  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grab" aria-hidden />
        <header className="sheet-head">
          <div className="sheet-head-pills">
            <span className={`pill pill-status-${ticket.status}`}>{ticket.status.replace('_', ' ')}</span>
            {c && <span className={`pill pill-${c.type}`}>{c.type}</span>}
            {c && <span className={`pill pill-${c.priority}`}>{c.priority}</span>}
          </div>
          <button className="sheet-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </header>

        <div className="sheet-body">
          <div className="sheet-time">{relativeTime(ticket.created_at)}</div>
          <h2 className="sheet-summary">
            {c?.summary || ticket.raw_input.text || '(no summary yet)'}
          </h2>

          {body && (
            <pre className="sheet-md">{body}</pre>
          )}

          {ticket.ship_queue_filename && (
            <div className="sheet-kv">
              <span className="sheet-kv-label">Ship queue file</span>
              <div className="sheet-kv-value">
                <code>{ticket.ship_queue_filename}</code>
                <button className="btn btn-ghost btn-small" onClick={() => copy('filename', ticket.ship_queue_filename!)}>
                  {copied === 'filename' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          <div className="sheet-kv">
            <span className="sheet-kv-label">Ticket ID</span>
            <div className="sheet-kv-value">
              <code>{ticket.id}</code>
              <button className="btn btn-ghost btn-small" onClick={() => copy('id', ticket.id)}>
                {copied === 'id' ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
